import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Inbox, Bug, Lightbulb, HelpCircle, Trash2, Building2, User as UserIcon, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '@/components/ui/alert-dialog';

type Estado = 'pendiente' | 'en_proceso' | 'completado';
type Tipo = 'error' | 'mejora' | 'pregunta';

interface Sugerencia {
  id: string;
  user_id: string;
  empresa_id: string | null;
  tipo: Tipo;
  mensaje: string;
  pantalla: string | null;
  estado: Estado;
  created_at: string;
}

const tipoMeta: Record<Tipo, { label: string; icon: any; color: string }> = {
  error: { label: 'Error', icon: Bug, color: 'bg-red-500/20 text-red-500 border-red-500/30' },
  mejora: { label: 'Mejora', icon: Lightbulb, color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30' },
  pregunta: { label: 'Pregunta', icon: HelpCircle, color: 'bg-blue-500/20 text-blue-500 border-blue-500/30' },
};

const estadoMeta: Record<Estado, { label: string; color: string }> = {
  pendiente: { label: 'Pendiente', color: 'bg-amber-500/20 text-amber-500 border-amber-500/30' },
  en_proceso: { label: 'En proceso', color: 'bg-purple-500/20 text-purple-500 border-purple-500/30' },
  completado: { label: 'Completado', color: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' },
};

const FeedbackPanel: React.FC = () => {
  const [items, setItems] = useState<Sugerencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState<'all' | Estado>('all');
  const [filtroTipo, setFiltroTipo] = useState<'all' | Tipo>('all');
  const [perfilesMap, setPerfilesMap] = useState<Record<string, { nombre: string; email: string }>>({});
  const [empresasMap, setEmpresasMap] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('sugerencias_app')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const list = (data || []) as Sugerencia[];
      setItems(list);

      const userIds = Array.from(new Set(list.map(i => i.user_id)));
      const empresaIds = Array.from(new Set(list.map(i => i.empresa_id).filter(Boolean) as string[]));

      if (userIds.length > 0) {
        const { data: perfiles } = await supabase
          .from('perfiles')
          .select('user_id, nombre, email')
          .in('user_id', userIds);
        const map: Record<string, { nombre: string; email: string }> = {};
        (perfiles || []).forEach((p: any) => { map[p.user_id] = { nombre: p.nombre, email: p.email }; });
        setPerfilesMap(map);
      }
      if (empresaIds.length > 0) {
        const { data: empresas } = await supabase
          .from('empresas')
          .select('id, nombre')
          .in('id', empresaIds);
        const map: Record<string, string> = {};
        (empresas || []).forEach((e: any) => { map[e.id] = e.nombre; });
        setEmpresasMap(map);
      }
    } catch (e: any) {
      console.error(e);
      toast.error('Error cargando sugerencias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter(i =>
      (filtroEstado === 'all' || i.estado === filtroEstado) &&
      (filtroTipo === 'all' || i.tipo === filtroTipo)
    );
  }, [items, filtroEstado, filtroTipo]);

  const stats = useMemo(() => ({
    total: items.length,
    pendiente: items.filter(i => i.estado === 'pendiente').length,
    en_proceso: items.filter(i => i.estado === 'en_proceso').length,
    completado: items.filter(i => i.estado === 'completado').length,
  }), [items]);

  const updateEstado = async (id: string, estado: Estado) => {
    const prev = items;
    setItems(items.map(i => i.id === id ? { ...i, estado } : i));
    const { error } = await supabase.from('sugerencias_app').update({ estado }).eq('id', id);
    if (error) {
      setItems(prev);
      toast.error('No se pudo actualizar');
    } else {
      toast.success('Estado actualizado');
    }
  };

  const handleDelete = async (id: string) => {
    const prev = items;
    setItems(items.filter(i => i.id !== id));
    const { error } = await supabase.from('sugerencias_app').delete().eq('id', id);
    if (error) {
      setItems(prev);
      toast.error('No se pudo eliminar');
    } else {
      toast.success('Sugerencia eliminada');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Inbox className="h-7 w-7 text-primary" />
          Buzón de feedback
        </h1>
        <p className="text-sm text-muted-foreground">
          Sugerencias, errores y preguntas enviados por los usuarios de todas las empresas.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-3"><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-bold">{stats.total}</p></Card>
        <Card className="p-3 border-amber-500/30"><p className="text-xs text-muted-foreground">Pendientes</p><p className="text-2xl font-bold text-amber-500">{stats.pendiente}</p></Card>
        <Card className="p-3 border-purple-500/30"><p className="text-xs text-muted-foreground">En proceso</p><p className="text-2xl font-bold text-purple-500">{stats.en_proceso}</p></Card>
        <Card className="p-3 border-emerald-500/30"><p className="text-xs text-muted-foreground">Completadas</p><p className="text-2xl font-bold text-emerald-500">{stats.completado}</p></Card>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Tabs value={filtroEstado} onValueChange={(v) => setFiltroEstado(v as any)}>
          <TabsList>
            <TabsTrigger value="all">Todas</TabsTrigger>
            <TabsTrigger value="pendiente">Pendientes</TabsTrigger>
            <TabsTrigger value="en_proceso">En proceso</TabsTrigger>
            <TabsTrigger value="completado">Completadas</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={filtroTipo} onValueChange={(v) => setFiltroTipo(v as any)}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="mejora">💡 Mejoras</SelectItem>
            <SelectItem value="error">🐞 Errores</SelectItem>
            <SelectItem value="pregunta">❓ Preguntas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground border border-dashed rounded-lg">
            <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Sin sugerencias para los filtros seleccionados.</p>
          </div>
        )}
        {filtered.map(item => {
          const TipoIcon = tipoMeta[item.tipo].icon;
          const perfil = perfilesMap[item.user_id];
          const empresa = item.empresa_id ? empresasMap[item.empresa_id] : null;
          return (
            <Card key={item.id} className="border-l-4 border-l-primary/40">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`gap-1 ${tipoMeta[item.tipo].color}`}>
                      <TipoIcon className="h-3 w-3" />
                      {tipoMeta[item.tipo].label}
                    </Badge>
                    <Badge variant="outline" className={estadoMeta[item.estado].color}>
                      {estadoMeta[item.estado].label}
                    </Badge>
                    {item.pantalla && (
                      <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {item.pantalla}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={item.estado} onValueChange={(v) => updateEstado(item.id, v as Estado)}>
                      <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="en_proceso">En proceso</SelectItem>
                        <SelectItem value="completado">Completado</SelectItem>
                      </SelectContent>
                    </Select>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar sugerencia?</AlertDialogTitle>
                          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                <p className="text-sm whitespace-pre-wrap">{item.mensaje}</p>

                <div className="flex items-center gap-x-4 gap-y-1 text-xs text-muted-foreground flex-wrap pt-1 border-t">
                  <span className="flex items-center gap-1">
                    <UserIcon className="h-3 w-3" />
                    {perfil?.nombre || 'Usuario'} {perfil?.email && <span className="opacity-70">· {perfil.email}</span>}
                  </span>
                  {empresa && (
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {empresa}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(item.created_at), "dd MMM yyyy HH:mm", { locale: es })}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default FeedbackPanel;
